<template>
    <aside
        v-if="visible"
        class="cookie-consent"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-consent-title"
    >
        <div class="cookie-consent__content">
            <h2 id="cookie-consent-title">Vos préférences de confidentialité</h2>
            <p>
                Nous utilisons, avec votre accord, des outils de mesure d’audience et de publicité.
                Les cookies nécessaires au fonctionnement du site restent toujours actifs.
                <a v-if="cookiePolicyUrl" :href="cookiePolicyUrl">En savoir plus</a>
            </p>

            <div v-if="customizing" class="cookie-consent__choices">
                <label>
                    <input type="checkbox" checked disabled>
                    Cookies nécessaires
                </label>
                <label>
                    <input v-model="analytics" type="checkbox">
                    Mesure d’audience
                </label>
                <label>
                    <input v-model="marketing" type="checkbox">
                    Publicité personnalisée
                </label>
            </div>

            <div class="cookie-consent__actions">
                <button type="button" class="btn btn-outline-secondary" @click="rejectAll">Tout refuser</button>
                <button
                    v-if="! customizing"
                    type="button"
                    class="btn btn-outline-primary"
                    @click="customizing = true"
                >
                    Personnaliser
                </button>
                <button
                    v-else
                    type="button"
                    class="btn btn-outline-primary"
                    @click="saveChoices"
                >
                    Enregistrer mes choix
                </button>
                <button type="button" class="btn btn-primary" @click="acceptAll">Tout accepter</button>
            </div>
        </div>
    </aside>
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
import tracking from '../modules/tracking.js';

const config = window.gtdriveTrackingConfig || {};
const enabled = computed(() => config.enabled === true);
const cookiePolicyUrl = computed(() => config.cookiePolicyUrl || null);
const visible = ref(false);
const customizing = ref(false);
const analytics = ref(false);
const marketing = ref(false);

const hydrate = () => {
    const consent = tracking.getConsent();
    analytics.value = consent?.analytics === true;
    marketing.value = consent?.marketing === true;
    visible.value = enabled.value && consent === null;
};

const close = () => {
    visible.value = false;
    customizing.value = false;
};

const persist = (choices) => {
    analytics.value = choices.analytics;
    marketing.value = choices.marketing;
    tracking.saveConsent(choices);
    close();
};

const acceptAll = () => persist({analytics: true, marketing: true});
const rejectAll = () => persist({analytics: false, marketing: false});
const saveChoices = () => persist({analytics: analytics.value, marketing: marketing.value});
const open = () => {
    hydrate();
    visible.value = true;
    customizing.value = true;
};

onMounted(() => {
    hydrate();
    window.addEventListener('gtdrive:consent-open', open);
});

onBeforeUnmount(() => window.removeEventListener('gtdrive:consent-open', open));
</script>

<style scoped>
.cookie-consent {
    position: fixed;
    inset: auto 0 0;
    z-index: 1090;
    padding: 1rem;
    background: rgba(16, 24, 40, 0.6);
}

.cookie-consent__content {
    max-width: 940px;
    margin: 0 auto;
    padding: 1.25rem;
    border-radius: 1rem;
    background: #fff;
    box-shadow: 0 -0.25rem 1.5rem rgba(0, 0, 0, 0.18);
}

.cookie-consent__content h2 {
    margin-bottom: 0.5rem;
    font-size: 1.35rem;
}

.cookie-consent__choices {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem 1.5rem;
    margin: 1rem 0;
}

.cookie-consent__choices label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.cookie-consent__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.5rem;
}


@media (max-width: 767.98px) {
    .cookie-consent__actions {
        display: grid;
        grid-template-columns: 1fr;
    }

    .cookie-consent__actions .btn {
        width: 100%;
    }
}
</style>
